// src/modules/finan/components/TableNfseRow.tsx
import { TableCell, TableRow, Typography, Checkbox, Chip, IconButton, Tooltip, Box } from "@mui/material";
import { ContactSupportOutlined, MoreVert } from "@mui/icons-material";
import { HtmlTooltip } from "../../../infra/components/tooltipsCustom/tooltipsCuston";
import { formatDateFnsRemoveTimezone } from "../../../shared/utils/format-date";
import { globalBackgroundNFSE, globalColorNFSE, globalMaskDocument } from "../../../shared/utils";
import { translateNFSEStatus } from "../utils/translateNFSEStatus";

interface Props {
	row: any;
	isSelected: boolean;
	isSelectable: boolean;
	onToggleRow: (id: string) => void;
	onOpenMenu: (e: React.MouseEvent<HTMLButtonElement>) => void;
}

export function TableNfseRow({ row, isSelected, isSelectable, onToggleRow, onOpenMenu }: Props) {
	const handleToggle = () => {
		if (isSelectable) onToggleRow(row.Installment_id);
	};

	return (
		<TableRow hover key={row.Installment_id} onClick={handleToggle} selected={isSelected} sx={{ cursor: isSelectable ? "pointer" : "default" }}>
			<TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
				<Checkbox checked={isSelected} disabled={!isSelectable} onChange={handleToggle} />
			</TableCell>

			<TableCell>
				<HtmlTooltip
					title={
						<Box>
							<Typography variant="subtitle2" fontWeight="bold">
								{row.tenant_name}
							</Typography>
						</Box>
					}
					arrow
					placement="top"
				>
					<Typography variant="body2" sx={{ maxWidth: "40ch", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
						{row.tenant_name || "Sem nome"}
					</Typography>
				</HtmlTooltip>
				<Typography variant="caption" color="text.secondary">
					{globalMaskDocument(row.tenant_document)}
				</Typography>
			</TableCell>

			<TableCell>
				<Tooltip title={row.client_name || "Nome não disponível"} arrow placement="top">
					<Box sx={{ maxWidth: 210, overflow: "hidden", textOverflow: "ellipsis" }}>
						<Typography variant="body2" noWrap>
							{row.client_name || "Sem nome"}
						</Typography>
					</Box>
				</Tooltip>
			</TableCell>

			<TableCell>
				{row.address_street ? (
					<>
						<HtmlTooltip
							title={
								<Box>
									<Typography variant="subtitle2" fontWeight="bold">
										Endereço completo
									</Typography>
									<Typography variant="body2">{`${row.address_street}, ${row.address_number} - ${row.address_district}`}</Typography>
									<Typography variant="body2">{`${row.address_city} - ${row.address_state}`}</Typography>
								</Box>
							}
							arrow
							placement="top"
						>
							<Box display="flex" alignItems="center">
								<Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis" }}>
									{`${row.address_street}, ${row.address_number} - ${row.address_district}`}
								</Typography>
							</Box>
						</HtmlTooltip>
						<Typography variant="caption" color="text.secondary">{`${row.address_city} - ${row.address_state}`}</Typography>
					</>
				) : (
					<Typography variant="body2" color="text.secondary">
						Endereço não cadastrado
					</Typography>
				)}
			</TableCell>

			<TableCell>
				<Typography variant="body2">{formatDateFnsRemoveTimezone(row.invoice_due_date)}</Typography>
			</TableCell>

			<TableCell>
				<Chip
					size="medium"
					label={translateNFSEStatus(row.nfse_status)}
					sx={{
						mt: 0.5,
						color: globalColorNFSE(row.nfse_status),
						fontWeight: 400,
						background: globalBackgroundNFSE(row.nfse_status),
						border: "0.5px solid #f1f1f1",
					}}
				/>

				{["issued", "authorized"].includes(row.nfse_status) && (
					<Typography variant="body2">{formatDateFnsRemoveTimezone(row.nfse_nfse_emissao)}</Typography>
				)}

				{row.nfse_nfse_mensagem && (
					<HtmlTooltip
						title={
							<Box>
								<Typography variant="subtitle2" fontWeight="bold">
									{row.nfse_nfse_situacao}
								</Typography>
								<Typography variant="body2">{row.nfse_nfse_mensagem || "Sem mensagem"}</Typography>
							</Box>
						}
						arrow
						placement="top"
					>
						<IconButton
							size="small"
							sx={{ ml: 0.5, color: "grey.500" }}
							aria-label="Detalhes da NFSE"
							onClick={(e) => {
								e.stopPropagation();
								navigator.clipboard.writeText(`${row.nfse_nfse_situacao ?? ""} - ${row.nfse_nfse_mensagem ?? ""}`);
							}}
						>
							<ContactSupportOutlined fontSize="inherit" />
						</IconButton>
					</HtmlTooltip>
				)}
			</TableCell>

			<TableCell>
				<Typography>
					{parseFloat(row.Installment_amount).toLocaleString("pt-br", {
						style: "currency",
						currency: "BRL",
					})}
				</Typography>
				<Typography variant="caption" color="text.secondary">
					{row.nfse_gateway}
				</Typography>
			</TableCell>

			<TableCell align="right">
				<IconButton
					size="small"
					onClick={(e) => {
						e.stopPropagation();
						onOpenMenu(e);
					}}
				>
					<MoreVert />
				</IconButton>
			</TableCell>
		</TableRow>
	);
}
